/**
 * A skill's glyph, as text -- or, for the few that no font draws reliably,
 * as a small line drawing in the text's own colour and size.
 *
 * Internal Library is a book (Huanrui, 2026-10-22): its grid was the Mood
 * board's too. There is no book character every machine draws in one
 * colour (U+1F4D6 is a colour emoji; U+1F56E is missing from most fonts),
 * so the skill's glyph is the emoji -- which still reads as a book anywhere
 * this component is not used -- and this draws it as a line icon.
 */
export const BOOK_GLYPH = '\u{1F4D6}'

/**
 * Clean up (2026-10-27): an eraser, drawn -- the glyph is the erase key,
 * which reads as one wherever this component is not used.
 */
export const ERASER_GLYPH = '\u232B'
/**
 * Background remove (2026-10-27): a frame of the see-through checks round a
 * subject, after Huanrui's reference. Its text glyph is a square half filled.
 */
export const CUTOUT_GLYPH = '\u25E9'

/**
 * Photo adjust (2026-10-30, was Colour grade): a camera, drawn. Its text
 * glyph is the camera emoji, which reads as one wherever this is not used.
 */
export const CAMERA_GLYPH = '\u{1F4F7}'

const SVG_STYLE = { display: 'inline-block', verticalAlign: '-0.18em', flex: 'none' } as const

/** The checks round the edge: two squares deep, every other one filled. */
const CHECKS: { x: number; y: number }[] = []
for (let i = 0; i < 8; i += 1) {
  for (let j = 0; j < 8; j += 1) {
    const edge = i < 2 || i > 5 || j < 2 || j > 5
    if (edge && (i + j) % 2 === 0) CHECKS.push({ x: i * 2, y: j * 2 })
  }
}

export function SkillGlyph({ glyph }: { glyph: string | null | undefined }) {
  if (glyph === CAMERA_GLYPH) {
    return (
      <svg
        className="ww-glyph-svg"
        viewBox="0 0 16 16"
        width="1.1em"
        height="1.1em"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={SVG_STYLE}
      >
        {/* The body with its hump, the lens, and the little window at the top right. */}
        <path d="M2 5.6c0-.6.5-1.1 1.1-1.1h1.8l1-1.6h4.2l1 1.6h1.8c.6 0 1.1.5 1.1 1.1v6.3c0 .6-.5 1.1-1.1 1.1H3.1c-.6 0-1.1-.5-1.1-1.1Z" />
        <circle cx="8" cy="8.6" r="2.4" />
        <path d="M11.9 6.4h.2" />
      </svg>
    )
  }
  if (glyph === ERASER_GLYPH) {
    return (
      <svg
        className="ww-glyph-svg"
        viewBox="0 0 16 16"
        width="1.1em"
        height="1.1em"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={SVG_STYLE}
      >
        {/* The rubber end, filled; the sleeve, outlined; the band between. */}
        <path d="M2.4 10.4 4.9 7.9 8.8 11.8 6.3 14.3Z" fill="currentColor" stroke="none" />
        <path d="M2.4 10.4 9 3.8 12.9 7.7 6.3 14.3Z" />
        <path d="M4.9 7.9 8.8 11.8" />
        {/* What it has just rubbed out. */}
        <path d="M9.6 14.3H14" />
      </svg>
    )
  }
  if (glyph === CUTOUT_GLYPH) {
    return (
      <svg
        className="ww-glyph-svg"
        viewBox="0 0 16 16"
        width="1.1em"
        height="1.1em"
        aria-hidden="true"
        style={SVG_STYLE}
      >
        {CHECKS.map((one) => (
          <rect key={`${one.x}-${one.y}`} x={one.x} y={one.y} width="2" height="2" fill="currentColor" />
        ))}
        <rect x="5.4" y="5.4" width="5.2" height="5.2" rx="1.3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    )
  }
  if (glyph === BOOK_GLYPH) {
    return (
      <svg
        className="ww-glyph-svg"
        viewBox="0 0 16 16"
        width="1.1em"
        height="1.1em"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ display: 'inline-block', verticalAlign: '-0.18em', flex: 'none' }}
      >
        <path d="M8 4.3C6.6 3.3 4.6 2.9 1.8 3.1v9.1c2.8-.2 4.8.2 6.2 1.2 1.4-1 3.4-1.4 6.2-1.2V3.1C11.4 2.9 9.4 3.3 8 4.3Z" />
        <path d="M8 4.3v9.1" />
      </svg>
    )
  }
  return <>{glyph ?? ''}</>
}
